// EPP Code mappings for ICHS Standard (two-letter codes)
export const EPP_ENVIRONMENT_CODES: Record<string, string> = {
  'Greenhouse': 'GH',
  'Hoophouse': 'HH',
  'Indoor': 'ID',
  'Mixed Light - Hybrid Greenhouse': 'ML',
  'Outdoor': 'OD',
};

export const EPP_PRODUCT_CODES: Record<string, string> = {
  'A-Flower': 'AF',
  'B-Flower': 'BF',
  'C-Flower': 'CF',
  'A/B Flower Mix': 'AB',
  'B/C Flower Mix': 'BC',
  'A/B/C Flower Mix': 'AC',
  'Fan Leaf': 'FL',
  'Fresh Frozen': 'FF',
  'Mixed Material': 'MM',
  'Trim': 'TR',
};

export const EPP_PROCESS_CODES: Record<string, string> = {
  'Drum Trimmed, Dry': 'DD',
  'Drum Trimmed, Wet': 'DW',
  'Machine Trimmed, Dry': 'MD',
  'Machine Trimmed, Wet': 'MW',
  'Partial Trim': 'PT',
  'Scissor Trimmed, Dry': 'SD',
  'Scissor Trimmed, Wet': 'SW',
  'Bucked, Untrimmed': 'BU',
  'Shucked, Untrimmed': 'SU',
};

// Reverse mappings for decoding
/** Retired EPP product names still found in older saved data → today's standard name. */
const LEGACY_EPP_PRODUCT_NAMES: Record<string, string> = {
  'A/B Mix': 'A/B Flower Mix',
  'B/C Mix': 'B/C Flower Mix',
  'A/B/C Mix': 'A/B/C Flower Mix',
};

/** Standard EPP product name for any saved value (old mix names included). */
export function normalizeEppProduct(product?: string | null): string | null {
  if (!product) return product ?? null;
  return LEGACY_EPP_PRODUCT_NAMES[product] ?? product;
}

const CODE_TO_ENVIRONMENT: Record<string, string> = Object.fromEntries(
  Object.entries(EPP_ENVIRONMENT_CODES).map(([k, v]) => [v, k])
);

const CODE_TO_PRODUCT: Record<string, string> = Object.fromEntries(
  Object.entries(EPP_PRODUCT_CODES).map(([k, v]) => [v, k])
);

const CODE_TO_PROCESS: Record<string, string> = Object.fromEntries(
  Object.entries(EPP_PROCESS_CODES).map(([k, v]) => [v, k])
);

/**
 * Generates a combined EPP code string (e.g., "IDAFSD")
 * Shows "--" for unspecified values, always returns a 6-character code
 */
export function getEPPCode(
  environment?: string | null,
  product?: string | null,
  process?: string | null
): string {
  const envCode = environment ? EPP_ENVIRONMENT_CODES[environment] || '--' : '--';
  const prodCode = product ? EPP_PRODUCT_CODES[normalizeEppProduct(product) as string] || '--' : '--';
  const procCode = process ? EPP_PROCESS_CODES[process] || '--' : '--';

  return `${envCode}${prodCode}${procCode}`;
}

/**
 * Decodes an EPP code into its component parts
 * Returns an object with environment, product, and process names
 */
export function decodeEPPCode(code: string): {
  environment: string | null;
  product: string | null;
  process: string | null;
} {
  if (!code || code.length !== 6) {
    return { environment: null, product: null, process: null };
  }

  const envCode = code.substring(0, 2);
  const prodCode = code.substring(2, 4);
  const procCode = code.substring(4, 6);

  return {
    environment: envCode !== '--' ? CODE_TO_ENVIRONMENT[envCode] || null : null,
    product: prodCode !== '--' ? CODE_TO_PRODUCT[prodCode] || null : null,
    process: procCode !== '--' ? CODE_TO_PROCESS[procCode] || null : null,
  };
}

// ── Bidirectional mapping: EPP Product ↔ Product Form (Biomass only) ──

/** Maps EPP Product labels to their canonical product_form slugs */
const EPP_PRODUCT_TO_FORM_VALUE: Record<string, string> = {
  'A-Flower':       'a-flower',
  'B-Flower':       'b-flower',
  'C-Flower':       'c-flower',
  'A/B Flower Mix':        'a/b flower mix',
  'B/C Flower Mix':        'b/c flower mix',
  'A/B/C Flower Mix':      'a/b/c flower mix',
  'Fan Leaf':       'fan leaf',
  'Fresh Frozen':   'fresh frozen',
  'Mixed Material': 'mixed material',
  'Trim':           'trim',
};

/** Reverse: product_form slug → EPP Product label */
const FORM_VALUE_TO_EPP_PRODUCT: Record<string, string> = Object.fromEntries(
  Object.entries(EPP_PRODUCT_TO_FORM_VALUE).map(([k, v]) => [v, k])
);

/**
 * Given a product_form slug (e.g. "a/b flower mix"), returns the matching
 * EPP Product label (e.g. "A/B Mix"), or null if no Biomass match exists.
 */
export function formValueToEppProduct(formValue: string): string | null {
  return FORM_VALUE_TO_EPP_PRODUCT[formValue.toLowerCase()] ?? null;
}

/**
 * Given an EPP Product label (e.g. "A/B Mix"), returns the matching
 * product_form slug (e.g. "a/b flower mix"), or null if not found.
 */
export function eppProductToFormValue(eppProduct: string): string | null {
  return EPP_PRODUCT_TO_FORM_VALUE[normalizeEppProduct(eppProduct) as string] ?? null;
}
